// Collect + group for the live-systems lens, on the factory's layers.
import { extractLiveOps } from "./index.js";
import { collectWindowOps, groupActivity, distinctKey } from "../../src/build/index.js";
import { describeState, reconstructState } from "../../src/build/reconstruct.js";
import { readServiceChange, readServiceState } from "./state.js";
import { serviceOf } from "./index.js";

export const extract = extractLiveOps;

const RANK = { answered: 3, raised: 2, refused: 2, unrecorded: 1 };
export const informedness = (op) => (RANK[op.outcome] || 0) * 10000 + (op.output?.length || 0);
export const collect = (prompts) => collectWindowOps(prompts, { informedness });

// The unit is the SERVICE in its environment — the thing a reader recognises and
// that outlives every session that touched it. Never the pod, whose name is
// regenerated on each deploy. A namespace-wide survey keys on the namespace,
// because that is genuinely what was being looked at.
export const groupBy = (op) => {
  const parts = [op.environment, op.namespace, op.service].filter(Boolean);
  return parts.length ? parts.join("/") : distinctKey("unscoped", op);
};

// Last-known state per service, folded from the PLATFORM's own schema only, plus
// whether each change was ever looked at again.
export const stateOf = (flat) => reconstructState(flat, {
  target: groupBy,
  at: (op) => op.ts,
  read: (op) => readServiceState(op, serviceOf),
  change: readServiceChange,
});

export const build = (flat) => {
  const recon = stateOf(flat);
  const byTarget = new Map(recon.targets.map((t) => [t.target, t]));
  const built = grouped(flat);
  for (const g of built.groups) {
    const entry = byTarget.get(g.key);
    if (!entry) continue;
    // Never without its timestamp: trace-derived state shown bare reads as current
    // fact when it is a historical observation.
    g.state = describeState(entry, { fields: ["ready", "status", "restarts", "pods"] });
    g.changes = entry.changes.map((c) => ({ what: c.what, at: c.at, checked: c.checked, stateDiffered: c.stateDiffered }));
    g.unchecked = entry.unchecked;
    if (g.state) g.labels = [g.state.split(" — ")[0], ...g.labels];
    // The finding worth surfacing above everything else in this lens: something was
    // changed and never looked at again.
    if (entry.unchecked) g.labels = [`${entry.unchecked} unchecked change${entry.unchecked > 1 ? "s" : ""}`, ...g.labels];
  }
  built.totals = { ...built.totals, ...recon.totals };
  return built;
};

const grouped = (flat) => groupActivity(flat, {
  key: groupBy,
  label: (op) => {
    const where = [op.environment, op.namespace, op.service].filter(Boolean).join("/");
    return where || "an unscoped system";
  },
  seed: () => ({ kinds: {}, outcomes: {}, labels: [], containers: [], probes: [], pods: new Set(), scopes: new Set() }),
  fold: (g, op) => {
    g.kinds[op.kind] = (g.kinds[op.kind] || 0) + 1;
    g.outcomes[op.outcome] = (g.outcomes[op.outcome] || 0) + 1;
    g.scopes.add(op.scope);
    if (op.container && !g.containers.includes(op.container)) g.containers.push(op.container);
    if (op.probe && !g.probes.includes(op.probe)) g.probes.push(op.probe.slice(0, 90));
    if (op.pod) g.pods.add(op.pod);
  },
  finish: (g) => {
    // How many distinct pods stood behind one service: the evidence that keying on
    // the pod would have shattered this group.
    g.labels = [
      g.pods.size > 1 ? `${g.pods.size} pods` : null,
      g.scopes.has("namespace") && !g.scopes.has("service") ? "namespace-wide" : null,
      ...g.containers.slice(0, 3),
    ].filter(Boolean);
    g.pods = g.pods.size;
    g.scopes = [...g.scopes];
  },
});

export const anchor = () => "kubectl";
export const ignore = ["output", "probe", "pod", "steps", "checks"];
